/* @flow */
import axios, { AxiosInstance } from 'axios';
import UrlAssembler from 'url-assembler';
import apiConfig from './apiConfig';
import LocalStorageService from '@/services/LocalStorageService';

export function createApiClient(csrfToken: ?string, baseUrl: string = ''): AxiosInstance {
  return axios.create({
    baseURL: baseUrl,
    headers: {
      /* eslint-disable quote-props */
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: csrfToken ? `Token ${csrfToken}` : '',
      /* eslint-enable quote-props */
    },
    validateStatus: (status) => {
      if (status === 401) {
        LocalStorageService.clear();
        window.location.href = '/login';
      }
      return status >= 200 && status < 300;
    },
  });
}

export function createUtilityApiClient(): AxiosInstance {
  return axios.create({
    baseURL: 'https://utilityapi.com',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${apiConfig.UAPI_TOKEN}`,
    },
  });
}

let uApiClient: AxiosInstance = createUtilityApiClient();
uApiClient.defaults.timeout = 200000;

let csrfToken: ?string = null;
let solarClient: AxiosInstance = createApiClient(csrfToken, apiConfig.BASE_URI);

export default {
  /**
   * Provide the actual Authorization token for all api clients
   *
   * @param authToken - actual Authorization token
   */
  init(authToken: string) {
    csrfToken = authToken;
    solarClient = createApiClient(csrfToken, apiConfig.BASE_URI);
    solarClient.defaults.timeout = 200000;
  },

  logIn(credentials: Object): Promise<any> {
    const logInUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/login/').toString();

    return solarClient.post(logInUrl, credentials);
  },

  signUp(data: Object): Promise<any> {
    const signUpUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/signup/').toString();

    return solarClient.post(signUpUrl, data);
  },

  createLead(data: Object): Promise<any> {
    const leadUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/lead/').toString();

    return solarClient.post(leadUrl, data);
  },

  fetchLeads(params: TableRequestParamsT = {}): Promise<any> {
    const leadsUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/leads/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(leadsUrl));
  },

  fetchLead(leadId: number): Promise<any> {
    const leadUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/lead/:lead/')
      .param('lead', leadId)
      .toString();

    return solarClient.get(leadUrl);
  },

  updateLead(leadId: number, data: Object): Promise<any> {
    const leadUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/update_lead/:lead/')
      .param('lead', leadId)
      .toString();

    return solarClient.patch(leadUrl, data);
  },

  fetchUapiData(referral: string): Promise<any> {
    const uApiUrl = new UrlAssembler()
      .prefix('/api/v2')
      .segment('/authorizations')
      .param('referrals', referral)
      .toString();

    return uApiClient.get(decodeURIComponent(uApiUrl));
  },

  fetchUapiFiles(data: Object): Promise<any> {
    const uApiFilesUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/utility_bill_from_service/')
      .toString();

    return solarClient.post(decodeURIComponent(uApiFilesUrl), data);
  },

  fetchLeadsSummary(): Promise<any> {
    const summaryUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/leads_summary/').toString();

    return solarClient.get(decodeURIComponent(summaryUrl));
  },

  sendInvite(data: Object): Promise<any> {
    const inviteUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/invited_users/').toString();

    return solarClient.post(decodeURIComponent(inviteUrl), data);
  },

  fetchInvites(params: TableRequestParamsT = {}): Promise<any> {
    const invitesUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/invited_users/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(invitesUrl));
  },

  fetchTokenData(token: string): Promise<any> {
    const tokenDataUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/invite/:token/')
      .param('token', token)
      .toString();

    return solarClient.get(decodeURIComponent(tokenDataUrl));
  },

  deleteLead(id: number): Promise<any> {
    const leadUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/delete_lead/:id/')
      .param('id', id)
      .toString();

    return solarClient.delete(leadUrl);
  },

  deleteLeadFile(url: string, id: number): Promise<any> {
    const fileUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment(`${url}/:id/`).param('id', id).toString();

    return solarClient.delete(decodeURIComponent(fileUrl));
  },

  getUserData(): Promise<any> {
    const userUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/user/').toString();

    return solarClient.get(decodeURIComponent(userUrl));
  },

  updateUser(data: Object): Promise<any> {
    const userUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/user/').toString();

    return solarClient.patch(userUrl, data);
  },

  updateOrganization(pk: number, data: Object): Promise<any> {
    const organizationUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/organization/:pk/')
      .param('pk', pk)
      .toString();

    return solarClient.patch(organizationUrl, data);
  },

  fetchProposals(params: TableRequestParamsT = {}): Promise<any> {
    const proposalsUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/proposal/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(proposalsUrl));
  },

  fetchProposal(proposalId: number, proposalInputsType: number): Promise<any> {
    const proposalUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/proposal/:proposalId/')
      .param('proposalId', proposalId)
      .query('pricing_inputs_type', proposalInputsType)
      .toString();

    return solarClient.get(proposalUrl);
  },

  createProposal(data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/proposal/').toString();

    return solarClient.post(decodeURIComponent(proposalUrl), data);
  },

  editProposal(id: number, data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/proposal/:id/')
      .param('id', id)
      .toString();

    return solarClient.patch(decodeURIComponent(proposalUrl), data);
  },

  generateProposal(data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/proposal_generate/').toString();

    return solarClient.post(decodeURIComponent(proposalUrl), data);
  },

  fetchResources(params: TableRequestParamsT = {}): Promise<any> {
    const resourcesUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/portal_resource/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(resourcesUrl));
  },

  uploadResource(data: ResourceT): Promise<any> {
    const resourcesUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/portal_resource/').toString();

    return solarClient.post(decodeURIComponent(resourcesUrl), data);
  },

  deleteResource(id: number): Promise<any> {
    const resourcesUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/portal_resource/:id/')
      .param('id', id)
      .toString();

    return solarClient.delete(decodeURIComponent(resourcesUrl));
  },

  fetchAgreements(params: TableRequestParamsT = {}): Promise<any> {
    const agreementsUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/agreement_for_sign/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(agreementsUrl));
  },

  createAgreement(data: Object): Promise<any> {
    const agreementUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/agreement/').toString();

    return solarClient.post(decodeURIComponent(agreementUrl), data);
  },

  resetPassword(data: Object): Promise<any> {
    const resetPasswordUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/reset_password/').toString();

    return solarClient.post(decodeURIComponent(resetPasswordUrl), data);
  },

  requestResetPassword(data: Object): Promise<any> {
    const resetPasswordUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/request_reset_password/')
      .toString();

    return solarClient.post(decodeURIComponent(resetPasswordUrl), data);
  },

  fetchOrganizations(): Promise<any> {
    const organizationsUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/organization_with_users/')
      .toString();

    return solarClient.get(decodeURIComponent(organizationsUrl));
  },

  fetchBulkEnrichments(params: TableRequestParamsT = {}): Promise<any> {
    const enrichmentsURl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/bulk_enrichment/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(enrichmentsURl));
  },

  uploadBulkEnrichmentFile(data: Object): Promise<any> {
    const enrichmentFileUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/bulk_enrichment/').toString();

    return solarClient.post(decodeURIComponent(enrichmentFileUrl), data);
  },

  deleteBulkEnrichmentFile(id: number): Promise<any> {
    const enrichmentFileUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/bulk_enrichment/:id/')
      .param('id', id)
      .toString();

    return solarClient.delete(decodeURIComponent(enrichmentFileUrl));
  },

  fetchProspectBulkEnrichments(params: TableRequestParamsT = {}): Promise<any> {
    const prospectEnrichmentsURl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/prospect_now_bulk_enrichment/')
      .param('page', params.page)
      .query({ ...params })
      .toString();

    return solarClient.get(decodeURIComponent(prospectEnrichmentsURl));
  },

  uploadProspectBulkEnrichmentFile(data: Object): Promise<any> {
    const prospectEnrichmentsURl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/prospect_now_bulk_enrichment/')
      .toString();

    return solarClient.post(decodeURIComponent(prospectEnrichmentsURl), data);
  },

  deleteProspectBulkEnrichmentFile(id: number): Promise<any> {
    const prospectEnrichmentsURl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/prospect_now_bulk_enrichment/:id/')
      .param('id', id)
      .toString();

    return solarClient.delete(decodeURIComponent(prospectEnrichmentsURl));
  },

  acceptTermsAndPolicy(data: Object): Promise<any> {
    const termsAndPolicyUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/accept_terms_and_policy/')
      .toString();

    return solarClient.post(decodeURIComponent(termsAndPolicyUrl), data);
  },

  createQuote(data: Object): Promise<any> {
    const quoteUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/quote/').toString();

    return solarClient.post(decodeURIComponent(quoteUrl), data);
  },

  createPaceQuote(data: Object): Promise<any> {
    const paceQuoteUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/quote/').toString();

    return solarClient.post(decodeURIComponent(paceQuoteUrl), data);
  },

  generateLeadProposal(data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/generate_quote/').toString();

    return solarClient.post(decodeURIComponent(proposalUrl), data);
  },

  fetchLeadEnrichmentStatus(leadId: number): Promise<any> {
    const leadUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/lead_enrichment_status/:lead/')
      .param('lead', leadId)
      .toString();
    return solarClient.get(decodeURIComponent(leadUrl));
  },

  fetchProjectInformationDetails(leadId: number): Promise<any> {
    const leadUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/get_project_information_details/:lead/')
      .param('lead', leadId)
      .toString();
    return solarClient.get(decodeURIComponent(leadUrl));
  },

  fetchDocument(filePath: string): Promise<any> {
    const fileUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/get_media_file/:filePath/')
      .param('filePath', filePath)
      .toString();
    return solarClient.get(decodeURIComponent(fileUrl));
  },

  setProposalActive(id: number, data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/quote/:id/')
      .param('id', id)
      .toString();

    return solarClient.patch(decodeURIComponent(proposalUrl), data);
  },

  requestProposal(id: number, data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler()
      .prefix(apiConfig.API_PREFIX)
      .segment('/quote/:id/')
      .param('id', id)
      .toString();

    return solarClient.patch(decodeURIComponent(proposalUrl), data);
  },

  unlockRequestedProposal(data: Object): Promise<any> {
    const proposalUrl = new UrlAssembler().prefix(apiConfig.API_PREFIX).segment('/unlock_proposal/').toString();

    return solarClient.post(decodeURIComponent(proposalUrl), data);
  },
};
